import { useState } from 'react'
import { AlertCircle, Loader2 } from 'lucide-react'
import { AuditTrailTable } from '@/components/audit/AuditTrailTable'
import { ManageProductsDialog } from '@/components/procedure/ManageProductsDialog'
import { LabelPrintingDialog } from '@/components/procedure/LabelPrintingDialog'
import { StorageAssignmentDialog } from '@/components/procedure/StorageAssignmentDialog'
import { MoveProductDialog } from '@/components/inventory/storage/MoveProductDialog'
import { ProductLabelPreviewDialog, storedSelectionsOf } from '@/components/procedure/ProductLabelPreviewDialog'
import type { ProductLabelSelections } from '@/lib/digitrax'
import { ShipProductDialog } from '@/components/shipping/ShipProductDialog'
import { usePermission, PERMISSIONS } from '@/lib/permissions'
import type { CreatedProduct } from '@/types/products'
import { PatientHistoryPanel } from '@/components/procedure/PatientHistoryPanel'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { usePatientRuns, useRunAuditTrail, useRunProducts } from '@/hooks/useProcedureBoard'
import type { BoardRun } from '@/lib/board/boardRuns'
import { cn } from '@/lib/utils'
import { PROCEDURE_TYPE_LABELS } from '@/types/cgt'

/**
 * The P dialog: every product created in the run.
 *
 * Printing a label opens the print popup right here: a reprint goes straight
 * to it with the stored messages, a different label goes through Label
 * Printing first. Both sign and record the label exactly as on the run's page.
 * Placing or moving a product opens its location dialog here too. Volume edits
 * and product history still open their own flows on the run's page.
 */
export function BoardProductsDialog({
  run,
  onClose,
  onOpenRun,
}: {
  run: BoardRun | null
  onClose: () => void
  onOpenRun: (run: BoardRun) => void
}) {
  const { data, isLoading } = useRunProducts(run?.procedure.id ?? null)
  const { can } = usePermission()
  // Shipping is its own dialog rather than a trip to the run: it is not a step
  // of any form, so there is nothing on the run's page to take the user to.
  const [shipping, setShipping] = useState<CreatedProduct | null>(null)
  // Label printing: choosing the messages for a different label, then the
  // print popup for a reprint or that new label.
  const [choosingLabel, setChoosingLabel] = useState<CreatedProduct | null>(null)
  const [printing, setPrinting] = useState<{
    product: CreatedProduct
    kind: 'reprint' | 'relabel'
    selections?: ProductLabelSelections
  } | null>(null)
  // Placing or moving a product is a dialog of its own, like shipping: leaving
  // the board for the run's page to do it sent people away from the product
  // they were looking at instead of opening its location.
  const [relocating, setRelocating] = useState<CreatedProduct | null>(null)
  const goToRun = () => {
    if (run) onOpenRun(run)
    onClose()
  }
  return (
    <>
      <ManageProductsDialog
        open={!!run && !shipping && !choosingLabel && !printing && !relocating}
        onClose={onClose}
        products={data ?? []}
        isLoading={isLoading}
        title='Products in this procedure'
        description='Everything created in this procedure, across its forms. Printing and shipping happen here; other actions open the procedure.'
        onReprint={product => setPrinting({ product, kind: 'reprint', selections: storedSelectionsOf(product) })}
        onChangeLabel={setChoosingLabel}
        onRelocate={setRelocating}
        onUpdateVolume={goToRun}
        onViewHistory={goToRun}
        onViewLims={goToRun}
        onShip={can(PERMISSIONS.productsShip) ? setShipping : undefined}
      />
      <LabelPrintingDialog
        product={choosingLabel}
        onClose={() => setChoosingLabel(null)}
        onPrinted={(product, selections) => {
          setChoosingLabel(null)
          setPrinting({ product, kind: 'relabel', selections })
        }}
      />
      <ProductLabelPreviewDialog
        product={printing?.product ?? null}
        selections={printing?.selections}
        printKind={printing?.kind}
        onClose={() => setPrinting(null)}
      />
      {/* A placed product is MOVED (shows where it is, never offers that slot);
          an unplaced one is PLACED from its suggested location. Both narrow to
          the product's mapped storage condition. */}
      <MoveProductDialog
        product={relocating?.storagePath ? relocating : null}
        onClose={() => setRelocating(null)}
      />
      <StorageAssignmentDialog
        product={relocating && !relocating.storagePath ? relocating : null}
        onClose={() => setRelocating(null)}
      />
      <ShipProductDialog
        open={!!shipping}
        onClose={() => setShipping(null)}
        initialProducts={shipping ? [shipping] : []}
      />
    </>
  )
}

/**
 * The H panel: the patient's other procedures, each with its progress. Opens at
 * once and fills in when the list arrives; a failed list says so and offers a
 * retry, so the panel can always be closed and reopened.
 */
export function BoardHistoryPanel({ run, onClose }: { run: BoardRun | null; onClose: () => void }) {
  const mrn = run?.procedure.patientMrn ?? null
  const { data, isPending, error, refetch } = usePatientRuns(mrn, run?.procedure.id ?? null)
  if (!run || !mrn) return null
  return (
    <PatientHistoryPanel
      open
      onOpenChange={open => !open && onClose()}
      procedures={data ?? []}
      loading={isPending}
      error={error}
      onRetry={() => void refetch()}
      patientMrn={mrn}
    />
  )
}

const FORM_STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  active: 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

/**
 * The run's audit trail, form by form. Each form's rows render through the
 * shared `AuditTrailTable`, so the columns match every other audit trail.
 */
export function RunAuditTrailDialog({ run, onClose }: { run: BoardRun | null; onClose: () => void }) {
  const { data, isLoading, error, refetch, isFetching } = useRunAuditTrail(run?.procedure.id ?? null)

  return (
    <Dialog open={!!run} onOpenChange={open => !open && onClose()}>
      {/* Header stays put; only the body scrolls, and each table scrolls sideways on its own. */}
      <DialogContent className='flex max-h-[85vh] flex-col overflow-hidden sm:max-w-6xl'>
        <DialogHeader className='shrink-0 pr-6'>
          <DialogTitle>Audit Trail</DialogTitle>
          <DialogDescription>
            {run
              ? `${run.procedure.procedureTemplate.name} · ${PROCEDURE_TYPE_LABELS[run.type]}. Every form in the procedure, form by form.`
              : ''}
          </DialogDescription>
        </DialogHeader>

        <div className='min-h-0 min-w-0 flex-1 space-y-4 overflow-y-auto'>
          <div className='flex justify-end'>
            <Button variant='ghost' size='sm' onClick={() => void refetch()} disabled={isFetching}>
              {isFetching && <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />}
              Refresh
            </Button>
          </div>

          {isLoading ? (
            <div className='flex items-center justify-center py-10 text-muted-foreground'>
              <Loader2 className='h-5 w-5 animate-spin' />
            </div>
          ) : error ? (
            <p className='flex items-center gap-2 text-sm text-destructive'>
              <AlertCircle className='h-4 w-4 shrink-0' />
              The audit trail could not be loaded.
            </p>
          ) : !data || data.length === 0 ? (
            <p className='py-6 text-center text-sm text-muted-foreground'>No forms have been opened in this procedure yet.</p>
          ) : (
            <div className='space-y-3'>
              {data.map(group => (
                <details key={group.submissionId} open={group.entries.length > 0} className='rounded-lg border'>
                  <summary className='flex cursor-pointer items-center gap-2 px-3 py-2 text-sm'>
                    <span className='font-medium'>{group.formName}</span>
                    <span
                      className={cn(
                        'rounded-full border px-2 py-0.5 text-[11px]',
                        group.status === 'completed'
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                          : 'border-slate-200 bg-slate-50 text-slate-600',
                      )}
                    >
                      {FORM_STATUS_LABEL[group.status] ?? group.status}
                    </span>
                    {group.isAdHoc && <span className='text-[11px] text-muted-foreground'>Added form</span>}
                    <span className='ml-auto text-xs text-muted-foreground'>
                      {group.entries.length} {group.entries.length === 1 ? 'change' : 'changes'}
                    </span>
                  </summary>
                  <div className='border-t p-3'>
                    <AuditTrailTable
                      rows={group.entries}
                      emptyMessage='No changes recorded yet.'
                      // Its own layout key, so narrowing this dialog does not
                      // narrow the form page's panel.
                      tableId='run-audit-trail-all-columns-v2'
                      maxHeightClass='max-h-[20rem]'
                    />
                  </div>
                </details>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
